/**
 * Clinician source localization path (Epic C2 / LS-G09 S5).
 * Production ESI jobs are gated; demo localize remains DEV-only and is
 * stripped from staging/production builds via allowClinicalPlaceholders().
 * Standalone nav entry is DB-driven: pick a study, load spike/seizure
 * annotations, then run assistive localize.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import AsyncStudySelect from "@shared/components/AsyncStudySelect";
import FeatureAccessAlert from "@shared/components/FeatureAccessAlert";
import usePhaseFeatureAccessGuard from "@shared/hooks/usePhaseFeatureAccessGuard";
import apiClient from "@shared/services/apiClient";
import { EegDockPanelLabeling } from "./EegDockPanelLabeling";
import {
  SOURCE_LOCALIZATION_LABELING,
  SOURCE_LOCALIZATION_PRODUCTION_ENABLED,
  SOURCE_LOCALIZATION_SKU,
  buildSourceLocalizationExportPackage,
  createSourceLocalizationJob,
  type SourceLocalizationExportPackage,
  type SourceLocalizationJob,
} from "../utils/sourceLocalizationClinician";
import { allowClinicalPlaceholders } from "../utils/clinicalPlaceholders";

type ClinicalEvent = {
  eventId: string;
  label: string;
  timeSeconds: number;
  kind: "spike" | "seizure_onset" | "other";
};

type AnnotationRow = {
  annotation_id?: string;
  id?: string;
  label?: string | null;
  annotation_type?: string | null;
  start_time?: number | string | null;
  reviewed?: boolean | null;
};

function loadDevOnlyDemoEvents(): ClinicalEvent[] {
  // Vite DCE: this block is eliminated when import.meta.env.DEV is false
  // (staging and production builds).
  if (!import.meta.env.DEV) {
    return [];
  }
  if (!allowClinicalPlaceholders()) {
    return [];
  }
  return [
    {
      eventId: "spk-1",
      label: "Demo spike @ 112s",
      timeSeconds: 112,
      kind: "spike",
    },
    {
      eventId: "sz-1",
      label: "Demo seizure onset @ 904s",
      timeSeconds: 904,
      kind: "seizure_onset",
    },
  ];
}

function classifyEventKind(
  annotationType: string,
  label: string,
): ClinicalEvent["kind"] {
  const hay = `${annotationType} ${label}`.toLowerCase();
  if (
    /seizure|ictus|onset|sz\b|clinical.?event/.test(hay) &&
    !/spike|ied|sharps?/.test(hay)
  ) {
    return "seizure_onset";
  }
  if (/spike|ied|epileptiform|sharp|interictal/.test(hay)) {
    return "spike";
  }
  return "other";
}

function isLocalizableAnnotation(annotation: AnnotationRow): boolean {
  const type = String(annotation.annotation_type || "");
  const label = String(annotation.label || "");
  const kind = classifyEventKind(type, label);
  return kind === "spike" || kind === "seizure_onset";
}

function mapAnnotationsToEvents(rows: AnnotationRow[]): ClinicalEvent[] {
  return rows
    .filter(isLocalizableAnnotation)
    .map((row) => {
      const eventId = String(row.annotation_id || row.id || "");
      const label = String(row.label || row.annotation_type || "Event").trim();
      const timeSeconds = Number(row.start_time);
      const kind = classifyEventKind(
        String(row.annotation_type || ""),
        label,
      );
      return {
        eventId,
        label: `${label} @ ${Number.isFinite(timeSeconds) ? timeSeconds : 0}s`,
        timeSeconds: Number.isFinite(timeSeconds) ? timeSeconds : 0,
        kind,
      };
    })
    .filter((event) => Boolean(event.eventId))
    .sort((a, b) => a.timeSeconds - b.timeSeconds);
}

const ESI_IFU_SUMMARY =
  "Source localization is investigational decision-support. It is not a standalone surgical plan.";

const ESI_IFU_EXCLUDED =
  "Excluded use: autonomous surgical targeting, unattended alarming, and treating demo or deferred ESI coordinates as validated localization evidence.";

const ESI_DEFERRAL_NOTE =
  "Production ESI jobs are not enabled in this release. Bundled localize remains a contract stub for development and labeling review only.";

const ESI_PRODUCTION_NOTE =
  "Production ESI jobs are enabled as assistive decision-support. Prefer patient-specific geometry when BEM/PACS co-reg is available. Confirm on raw EEG and imaging.";

export default function SourceLocalizationPanel(props?: {
  studyId?: string;
  events?: ClinicalEvent[];
}) {
  const {
    accessLoading,
    accessDenied,
    accessDetail,
    accessReason,
    refreshAccess,
  } = usePhaseFeatureAccessGuard({
    phase: 3,
    featureKey: "SOURCE_LOCALIZATION",
    featureUnavailableDetail:
      "Source localization (ESI) is not enabled for this organization.",
    missingOrgDetail:
      "Authenticated organization context is required to access source localization.",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const productionEnabled = SOURCE_LOCALIZATION_PRODUCTION_ENABLED;
  const placeholdersAllowed = allowClinicalPlaceholders();

  const urlStudyId =
    searchParams.get("studyId") ||
    searchParams.get("eegStudyId") ||
    "";

  const [studyId, setStudyId] = useState(
    props?.studyId || urlStudyId || (placeholdersAllowed ? "epi-study-demo" : ""),
  );
  const [loadedEvents, setLoadedEvents] = useState<ClinicalEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventId, setEventId] = useState("");
  const [geometryMode, setGeometryMode] = useState<
    "template_mri" | "patient_specific"
  >("template_mri");
  const [jobs, setJobs] = useState<SourceLocalizationJob[]>([]);
  const [pkg, setPkg] = useState<SourceLocalizationExportPackage | null>(null);

  const propEvents = props?.events?.length ? props.events : null;
  const demoEvents =
    !propEvents && !studyId && placeholdersAllowed
      ? loadDevOnlyDemoEvents()
      : [];
  const events = propEvents || (studyId ? loadedEvents : demoEvents);
  const usingDevPlaceholders =
    !propEvents && !studyId && placeholdersAllowed && demoEvents.length > 0;
  const hasStudyContext = Boolean(studyId.trim());
  const allowRun =
    events.length > 0 &&
    hasStudyContext &&
    (productionEnabled || placeholdersAllowed);

  const loadStudyEvents = useCallback(async (nextStudyId: string) => {
    const trimmed = nextStudyId.trim();
    if (!trimmed) {
      setLoadedEvents([]);
      setEventsError(null);
      setEventId("");
      return;
    }
    setEventsLoading(true);
    setEventsError(null);
    try {
      const response = await apiClient.get("/eeg-annotations", {
        params: { studyId: trimmed },
      });
      const data = response.data?.data || response.data || [];
      const rows = Array.isArray(data) ? (data as AnnotationRow[]) : [];
      const mapped = mapAnnotationsToEvents(rows);
      setLoadedEvents(mapped);
      setEventId((prev) =>
        mapped.some((event) => event.eventId === prev)
          ? prev
          : mapped[0]?.eventId || "",
      );
      if (!mapped.length) {
        setEventsError(
          "No spike or seizure-onset annotations found for this study. Confirm events in the EEG viewer AI Triage / annotations, then reload.",
        );
      }
    } catch (err: unknown) {
      setLoadedEvents([]);
      setEventId("");
      setEventsError(
        err instanceof Error
          ? err.message
          : "Failed to load study annotations for ESI",
      );
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (propEvents?.length) {
      setEventId(propEvents[0].eventId);
      return;
    }
    if (studyId.trim() && studyId !== "epi-study-demo") {
      void loadStudyEvents(studyId);
      return;
    }
    if (usingDevPlaceholders) {
      setEventId(demoEvents[0]?.eventId || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / study change
  }, [studyId, propEvents]);

  const selected = useMemo(
    () => events.find((event) => event.eventId === eventId) ?? events[0],
    [eventId, events],
  );

  const onStudyChange = (nextStudyId: string) => {
    setStudyId(nextStudyId);
    setJobs([]);
    setPkg(null);
    const next = new URLSearchParams(searchParams);
    if (nextStudyId.trim()) next.set("studyId", nextStudyId.trim());
    else next.delete("studyId");
    next.delete("eegStudyId");
    setSearchParams(next, { replace: true });
  };

  const localize = (partnerBridge = false) => {
    if (!allowRun || !selected || !studyId.trim()) {
      return;
    }
    const job = createSourceLocalizationJob({
      studyId: studyId.trim(),
      eventId: selected.eventId,
      eventKind: selected.kind,
      timeSeconds: selected.timeSeconds,
      geometryMode,
      partnerBridge,
    });
    setJobs((prev) => [...prev, job]);
  };

  const exportPkg = () => {
    if (!jobs.length || !studyId.trim()) {
      return;
    }
    setPkg(
      buildSourceLocalizationExportPackage({
        studyId: studyId.trim(),
        jobs,
      }),
    );
  };

  if (accessDenied) {
    return (
      <FeatureAccessAlert
        reason={accessReason}
        detail={accessDetail || "Source localization is not available."}
        onRetry={() => void refreshAccess()}
      />
    );
  }

  if (accessLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">
          Checking source localization entitlement...
        </p>
      </div>
    );
  }

  return (
    <Container fluid className="py-3" data-testid="source-localization-panel">
      <Row className="g-3">
        <Col md={12}>
          <EegDockPanelLabeling
            data-testid="esi-labeling"
            badge={productionEnabled ? "Investigational" : "Deferred"}
            summary={ESI_IFU_SUMMARY}
            intendedUse={SOURCE_LOCALIZATION_LABELING}
            excludedUse={ESI_IFU_EXCLUDED}
            sessionNotes={
              <span data-testid="esi-deferral-note">
                {productionEnabled
                  ? ESI_PRODUCTION_NOTE
                  : ESI_DEFERRAL_NOTE}
                {usingDevPlaceholders
                  ? " Local-dev demo events are active and are not included in staging/production builds."
                  : ""}
              </span>
            }
          />
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>Select spike / seizure onset</Card.Header>
            <Card.Body>
              {!productionEnabled ? (
                <Alert variant="warning" data-testid="esi-production-gate">
                  {ESI_DEFERRAL_NOTE}
                </Alert>
              ) : null}

              {!propEvents ? (
                <Form.Group className="mb-3" data-testid="esi-study-select">
                  <Form.Label>EEG study</Form.Label>
                  <AsyncStudySelect
                    value={studyId === "epi-study-demo" ? "" : studyId}
                    onChange={onStudyChange}
                    valueKind="study"
                    placeholder="Search patient, MRN, or study…"
                    clearLabel="Clear selected study"
                  />
                  <Form.Text className="text-muted">
                    Choose a study to load confirmed spike / seizure annotations
                    from the database.
                  </Form.Text>
                </Form.Group>
              ) : null}

              {eventsLoading ? (
                <div className="mb-3" data-testid="esi-events-loading">
                  <Spinner size="sm" animation="border" className="me-2" />
                  Loading study events…
                </div>
              ) : null}

              {eventsError ? (
                <Alert variant="info" className="mb-3" data-testid="esi-awaiting-events">
                  {eventsError}
                </Alert>
              ) : null}

              {productionEnabled &&
              hasStudyContext &&
              !eventsLoading &&
              !events.length &&
              !eventsError ? (
                <Alert variant="info" data-testid="esi-awaiting-events">
                  Select confirmed spikes from AI Triage or spike-average
                  clusters to run patient-specific / template ESI.
                </Alert>
              ) : null}

              {usingDevPlaceholders ? (
                <Alert variant="secondary" data-testid="esi-dev-placeholders">
                  Demo events are local-development only (SKU{" "}
                  {SOURCE_LOCALIZATION_SKU.skuId}). They are not shipped in
                  staging or production builds.
                </Alert>
              ) : null}

              <Form.Label>Clinical event</Form.Label>
              <Form.Select
                className="mb-2"
                value={eventId}
                onChange={(event) => setEventId(event.target.value)}
                data-testid="esi-event"
                disabled={!events.length || eventsLoading}
              >
                {!events.length ? (
                  <option value="">No localizable events loaded</option>
                ) : null}
                {events.map((event) => (
                  <option key={event.eventId} value={event.eventId}>
                    {event.label}
                  </option>
                ))}
              </Form.Select>

              <Form.Label>Head geometry</Form.Label>
              <Form.Select
                className="mb-3"
                value={geometryMode}
                onChange={(event) =>
                  setGeometryMode(
                    event.target.value as "template_mri" | "patient_specific",
                  )
                }
                data-testid="esi-geometry"
                disabled={!hasStudyContext && !usingDevPlaceholders}
              >
                <option value="template_mri">Template MRI geometry</option>
                <option value="patient_specific">Patient-specific geometry</option>
              </Form.Select>

              <Button
                className="me-2"
                onClick={() => localize(false)}
                data-testid="esi-run"
                disabled={!allowRun}
                title={
                  allowRun
                    ? productionEnabled
                      ? "Run assistive ESI job (template or patient geometry)"
                      : "Run bundled contract stub (not production ESI)"
                    : "Select a study with spike/seizure annotations"
                }
              >
                Localize {productionEnabled ? "(assistive)" : "(bundled)"}
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => localize(true)}
                data-testid="esi-partner-bridge"
                disabled={!allowRun}
                title={
                  allowRun
                    ? "Partner CURRY-class bridge handoff"
                    : "Select a study with spike/seizure annotations"
                }
              >
                Partner bridge
              </Button>
              {!hasStudyContext && !usingDevPlaceholders ? (
                <p className="text-muted small mt-3 mb-0" data-testid="esi-empty-state">
                  Select an EEG study above, or open this page from an EEG viewer
                  ESI handoff with confirmed spike events.
                </p>
              ) : !allowRun ? (
                <p className="text-muted small mt-3 mb-0" data-testid="esi-empty-state">
                  Waiting for localizable annotations on the selected study.
                  Confirm spikes in AI Triage, then use Reload events.
                </p>
              ) : usingDevPlaceholders ? (
                <p className="text-muted small mt-3 mb-0">
                  Demo events only — confirm any material finding on raw EEG. Do
                  not use stub coordinates for surgical planning.
                </p>
              ) : (
                <p className="text-muted small mt-3 mb-0">
                  Clinical events loaded — confirm findings on raw EEG and
                  imaging.
                </p>
              )}
              {hasStudyContext && studyId !== "epi-study-demo" ? (
                <Button
                  variant="link"
                  size="sm"
                  className="px-0 mt-2"
                  disabled={eventsLoading}
                  onClick={() => void loadStudyEvents(studyId)}
                  data-testid="esi-reload-events"
                >
                  Reload events
                </Button>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              3D / dipole viewport
              {placeholdersAllowed ? " (dev preview)" : ""}
            </Card.Header>
            <Card.Body
              className="bg-dark text-light text-center py-5"
              data-testid="esi-viewport"
            >
              {jobs.length
                ? `${jobs[jobs.length - 1].peakLabel} - ${jobs[jobs.length - 1].geometryMode}`
                : productionEnabled
                  ? "Run localization or dipole analysis to preview"
                  : "ESI production viewport deferred"}
            </Card.Body>
          </Card>
          <ListGroup className="mb-3" data-testid="esi-jobs">
            {jobs.map((job) => (
              <ListGroup.Item key={job.jobId}>
                <Badge bg="secondary" className="me-2">
                  {job.method}
                </Badge>
                {job.eventId} @ {job.timeSeconds}s - {job.geometryMode} - ±
                {job.uncertaintyMm.x}mm
                {job.investigationalUse ? " · investigational" : ""}
              </ListGroup.Item>
            ))}
          </ListGroup>
          <Button
            disabled={!jobs.length}
            onClick={exportPkg}
            data-testid="esi-export"
          >
            Export planning package
          </Button>
          {pkg ? (
            <Alert variant="success" className="mt-2" data-testid="esi-package">
              {pkg.packageId}: {pkg.geometryDisclosure}
            </Alert>
          ) : null}
        </Col>
      </Row>
    </Container>
  );
}
